import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { API } from "@/lib/api";

const sessionId = `web-${Math.random().toString(36).slice(2)}-${Date.now()}`;

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi, I'm the Go Big Forever Concierge 👋 Ask me about products, shipping to NZ/AU, GST, returns, or selling with us.",
    },
  ]);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data:\s*/, "").trim();
          if (!line) continue;
          try {
            const data = JSON.parse(line);
            if (data.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  content: copy[copy.length - 1].content + data.delta,
                };
                return copy;
              });
            }
          } catch (err) {
            if (process.env.NODE_ENV !== "production") console.debug("chat: skipped malformed SSE chunk", err);
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't connect. Please try again." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-toggle-btn"
        onClick={() => setOpen(!open)}
        className="fixed bottom-24 right-6 z-50 bg-brand text-white w-14 h-14 rounded-full shadow-xl flex items-center justify-center hover:bg-brand-hover transition-colors"
        aria-label="Open support chat"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {open && (
        <div
          data-testid="chat-window"
          className="fixed bottom-40 right-6 z-50 w-[92vw] max-w-sm h-[28rem] bg-white border border-ink shadow-2xl flex flex-col animate-fade-up"
        >
          <div className="bg-ink text-white px-4 py-3 flex items-center gap-3">
            <img
              src="https://images.unsplash.com/photo-1758600436605-7e43ac56f707?crop=entropy&cs=srgb&fm=jpg&q=85&w=120"
              alt="Concierge"
              className="w-9 h-9 rounded-full object-cover"
            />
            <div>
              <p className="font-display font-bold text-sm leading-none">Go Big Forever Concierge</p>
              <p className="text-[11px] text-white/60">AI support · usually instant</p>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 no-scrollbar">
            {messages.map((m, i) => (
              <div
                key={`${i}-${m.role}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[80%] px-3 py-2 text-sm leading-relaxed ${
                    m.role === "user" ? "bg-brand text-white" : "bg-secondary text-ink"
                  }`}
                >
                  {m.content || <span className="opacity-40">…</span>}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-ink/10 p-3 flex items-center gap-2">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type your question…"
              className="flex-1 text-sm bg-transparent border-b border-ink/20 focus:border-brand outline-none py-2"
            />
            <button
              data-testid="chat-send-btn"
              onClick={send}
              disabled={busy}
              className="bg-ink text-white w-9 h-9 flex items-center justify-center hover:bg-brand transition-colors disabled:opacity-40"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
